import * as React from "react";
import { useTranslation } from "react-i18next";
import { config as reactTransitionGroupConfig } from "react-transition-group";
import "tslib";
import { DialogManager, invariant, ToastManager } from "@plans/appkit";
import { WifiOffIcon } from "@plans/icons/untitled-ui/WifiOffIcon";
import * as Sentry from "@sentry/react-router";
import { posthog } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useRouteLoaderData,
} from "react-router";
import appCSS from "~/styles/app.css?url";
import type { Route } from "./+types/root";
import { useConsents } from "./analytics/usercentrics";
import { errorToErrorResponse } from "./api/mappings.server";
import { GeneralErrorBoundary } from "./components/molecules/GeneralErrorBoundary";
import { ManagedDialog } from "./components/molecules/ManagedDialog";
import { ManagedToasts } from "./components/molecules/ManagedToasts";
import type { ClientEnvironment } from "./environment";
import type { SupportedLocale } from "./i18n/config";
import { supportedLocales } from "./i18n/config";
import { i18nCookie, serverI18n } from "./i18n/config.server";
import {
	getServerTiming,
	ServerTimingSegment,
} from "./lib/server-timing/server-timing.server";
import { DialogContext } from "./lib/ui/DialogContext";
import { ToastContext } from "./lib/ui/ToastContext";
import { useIsHydrated } from "./lib/ui/useIsHydrated";
import { useIsOffline } from "./lib/ui/useIsOffline";
import { getEnvironment } from "./lib/utils/get-environment";
import { isInternalEmployee } from "./lib/utils/user-helpers";
import { logger } from "./logging";
import { getAuthenticatedUser } from "./sessions/auth-session.server";
import {
	A_VERY_VERY_VERY_VERY_LARGE_NUMBER,
	DEFAULT_TOAST_LIFETIME,
} from "./ui/constants";
import type { ToastVariant } from "./ui/types";
import { useFeatureFlags } from "./ui/useFeatureFlags";

export const links: Route.LinksFunction = () => [
	{ rel: "stylesheet", href: appCSS },
];

export const meta: Route.MetaFunction = () => [
	{
		title: "Plans",
	},
];

export async function loader({ request, context, params }: Route.LoaderArgs) {
	try {
		const locale = await serverI18n.getLocale(request);
		const user = await getServerTiming(context).measure(
			ServerTimingSegment.auth,
			() => getAuthenticatedUser(request),
		);
		const headers = new Headers();
		const searchParamsLocale = new URL(request.url).searchParams.get("lng");

		if (
			searchParamsLocale &&
			supportedLocales.includes(searchParamsLocale as SupportedLocale)
		) {
			headers.append(
				"Set-Cookie",
				await i18nCookie.serialize(searchParamsLocale),
			);
		}

		const environment: ClientEnvironment = {
			// ⚠️ CAREFUL: Whatever we expose here will be visible from the client! ⚠️
			MAPBOX_ACCESS_TOKEN: process.env.MAPBOX_ACCESS_TOKEN,
			SENTRY_DSN: process.env.SENTRY_DSN,
			SENTRY_RELEASE: process.env.SENTRY_RELEASE,
			POSTHOG_WRITE_KEY: process.env.POSTHOG_WRITE_KEY,
			USERCENTRICS_SETTINGS_ID: process.env.USERCENTRICS_SETTINGS_ID,
			NODE_ENV: process.env.NODE_ENV,
			IS_STAGING: process.env.IS_STAGING,
		};

		// Cannot use successResponse in root because i18n expects `locale` at the
		// top level of the loader data, not nested under `body`. We return raw
		// `data()` here so single fetch streams the fields with `Set-Cookie`
		// headers preserved.
		return data(
			{
				// ⚠️ CAREFUL: Whatever we expose here will be visible from the client! ⚠️
				env: environment,
				locale: locale,
				posthogWriteKey: process.env.POSTHOG_WRITE_KEY,
				me: user?.me,
			},
			{ headers },
		);
	} catch (error) {
		throw errorToErrorResponse(error);
	}
}

let isPostHogIdentified = false;
posthog.opt_out_capturing();

/**
 * App-wide context providers and their managed singletons. Kept inside
 * `Layout` (wrapping `children`) so both the routed `App` tree and the root
 * `ErrorBoundary` can use `useDialog()` / `useToast()`.
 */
function RootProviders({ children }: { children: React.ReactNode }) {
	const { t } = useTranslation(["common"]);

	const dialogDefaultProps = React.useMemo(
		() => ({
			okLabel: t("common:ok"),
			cancelLabel: t("common:cancel"),
		}),
		[t],
	);

	const dialogManager = React.useMemo(() => {
		return new DialogManager<React.ReactNode | string>(dialogDefaultProps);
	}, [dialogDefaultProps]);

	const toastManager = React.useMemo(() => {
		return new ToastManager<ToastVariant, React.ReactNode>({
			defaultLifetime: DEFAULT_TOAST_LIFETIME,
		});
	}, []);

	return (
		<ToastContext value={toastManager}>
			<DialogContext value={dialogManager}>
				<PostHogProvider client={posthog}>
					{children}
					<ManagedDialog />
					<ManagedToasts />
				</PostHogProvider>
			</DialogContext>
		</ToastContext>
	);
}

/**
 * Client-only side effects for the whole app (analytics, error attribution,
 * offline notice, hydration marker). Runs from the routed `App` component so
 * the lean `Layout` shell stays free of app logic.
 */
function useRootClientEffects(loaderData: Route.ComponentProps["loaderData"]) {
	const { i18n, t } = useTranslation(["common"]);
	const me = loaderData?.me;
	const locale = loaderData?.locale;
	const consents = useConsents();
	const isHydrated = useIsHydrated();
	const isOffline = useIsOffline();
	// Grab the manager directly (instead of `useToast()`) so this root-level
	// hook doesn't subscribe to toast state — otherwise every toast shown
	// anywhere in the app would re-render the tree from the root.
	const toastManager = React.useContext(ToastContext);
	invariant(toastManager, "Use within a ToastContext");
	const [isOfflineToastId, setIsOfflineToastId] = React.useState<number>();
	const [isPosthogInitialized, setIsPosthogInitialized] = React.useState(false);
	const { isDebuggerEnabled } = useFeatureFlags();

	React.useEffect(() => {
		i18n.changeLanguage(locale);
	}, [locale, i18n]);

	React.useEffect(() => {
		// Set html className hydrated when client is hydrated
		// This is useful for E2E tests to know when the client is ready.
		// Based on https://github.com/robipop22/dnb-stack/issues/3#issuecomment-1463031001
		if (isHydrated) {
			document.documentElement.classList.add("hydrated");
		}
	}, [isHydrated]);

	React.useEffect(() => {
		if (!isOffline && isOfflineToastId) {
			toastManager.clearToast(isOfflineToastId);
			setIsOfflineToastId(undefined);
		} else if (isOffline && !isOfflineToastId) {
			const id = toastManager.toast({
				heading: t("common:toast.offline_heading"),
				body: t("common:toast.offline_body"),
				variant: "offline",
				icon: <WifiOffIcon />,
				lifetime: A_VERY_VERY_VERY_VERY_LARGE_NUMBER,
			});
			setIsOfflineToastId(id);
		}
	}, [isOffline, toastManager, isOfflineToastId, t]);

	// Only purpose of this effect is to initialize/reset posthog whenever the
	// user's consent changes. Other effects then control what should happen when
	// posthog has been fully initialized.
	React.useEffect(() => {
		if (!window.env.POSTHOG_WRITE_KEY) {
			return;
		}
		if (consents.posthog) {
			if (isPosthogInitialized) {
				return;
			}

			posthog.init(window.env.POSTHOG_WRITE_KEY, {
				api_host: "https://eu.posthog.com",
				// Capture Core Web Vitals (LCP/INP/CLS/FCP) as `$web_vitals`
				// events. This only runs once consent is granted (the effect is
				// gated on `consents.posthog`), so web vitals never fire before
				// consent — same guarantee as every other capture here. Gives us
				// the client-side RUM we were missing for the RR7/single-fetch
				// latency work (NG-4296).
				capture_performance: {
					web_vitals: true,
				},
				session_recording: {
					maskTextSelector: null,
					maskAllInputs: true,
					captureCanvas: {
						recordCanvas: true,
						canvasFps: 1,
						canvasQuality: "0.25",
					},
				},
				loaded: () => setIsPosthogInitialized(true),
			});
		} else {
			posthog.opt_out_capturing();
			posthog.reset();
			setIsPosthogInitialized(false);
			isPostHogIdentified = false;
		}
	}, [consents.posthog, isPosthogInitialized]);

	// Controls what happens when posthog has been fully initialized.
	React.useEffect(() => {
		if (!isPosthogInitialized) {
			return;
		}
		const commonProperties = {
			source: "kelvin",
			// NB: We have separate Posthog projects for staging and production,
			// so we do not need to send the environment however, it is useful to
			// have it in the event properties for debugging purposes.
			environment: getEnvironment(window.env.NODE_ENV, window.env.IS_STAGING),
		} as const;
		if (me) {
			if (isPostHogIdentified) {
				return;
			}
			logger.info("Identifying user");
			posthog.identify(me.id, { email: me.email });
			posthog.group("installerCompany", me.installerCompany.id);
			posthog.register({
				...commonProperties,
				userEmail: me.email,
				userId: me.id,
				installerCompanySlug: me.installerCompany.slug,
				isPlansUser: isInternalEmployee([
					me.email,
					me.installerCompany.primaryContactEmail,
				]),
			});
			isPostHogIdentified = true;
		} else {
			logger.info("Registering unauthenticated user");
			posthog.register({
				...commonProperties,
			});
			isPostHogIdentified = false;
		}
	}, [me, isPosthogInitialized]);

	React.useEffect(() => {
		if (isDebuggerEnabled && consents.posthog && !posthog.config.debug) {
			posthog.debug(true);
		} else if (posthog.config.debug) {
			posthog.debug(false);
		}
	}, [isDebuggerEnabled, consents.posthog]);

	React.useEffect(() => {
		if (globalThis.window?.__E2E__) {
			reactTransitionGroupConfig.disabled = true;
		}
	}, []);

	React.useEffect(() => {
		if (!me) {
			return;
		}
		Sentry.setUser({
			id: me.id,
			email: me.email,
		});
	}, [me]);
}

export function Layout({ children }: { children: React.ReactNode }) {
	const rootRouteLoaderData = useRouteLoaderData<typeof loader>("root");
	const locale = rootRouteLoaderData?.locale;

	return (
		<html lang={locale}>
			<head>
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width,initial-scale=1,maximum-scale=1.0,viewport-fit=cover"
				/>
				<meta
					name="description"
					content="Our homes need to be decarbonised quickly. At Plans, we remove the friction that stands in the way."
				/>
				<meta name="og:type" content="website" />
				<meta name="og:title" content="Plans – Decarbonising homes, faster." />
				<meta
					name="og:description"
					content="Our homes need to be decarbonised quickly. At Plans, we remove the friction that stands in the way."
				/>
				<meta name="twitter:card" content="summary_large_image" />
				<meta
					name="twitter:title"
					content="Plans – Decarbonising homes, faster."
				/>
				<meta
					name="twitter:description"
					content="Our homes need to be decarbonised quickly. At Plans, we remove the friction that stands in the way."
				/>
				<link
					rel="icon"
					href="/favicon-light.ico"
					media="(prefers-color-scheme: light)"
				/>
				<link
					rel="icon"
					href="/favicon-dark.ico"
					media="(prefers-color-scheme: dark)"
				/>
				<link
					rel="apple-touch-icon"
					sizes="180x180"
					href="/apple-touch-icon-180.png"
				/>
				<link rel="icon" type="image/png" sizes="32x32" href="/icon-32.png" />
				<link rel="icon" type="image/png" sizes="16x16" href="/icon-16.png" />
				<link rel="icon" type="image/png" sizes="48x48" href="/icon-48.png" />
				<link rel="icon" type="image/png" sizes="64x64" href="/icon-64.png" />
				<link
					rel="icon"
					type="image/png"
					sizes="192x192"
					href="/icon-192.png"
				/>
				<link
					rel="icon"
					type="image/png"
					sizes="512x512"
					href="/icon-512.png"
				/>
				<Meta />
				<Links />
			</head>
			<body>
				<RootProviders>{children}</RootProviders>
				<ScrollRestoration />
				<script
					dangerouslySetInnerHTML={{
						__html: `window.env = ${JSON.stringify(rootRouteLoaderData?.env)}`,
					}}
				/>
				<Scripts />
			</body>
		</html>
	);
}

// `@sentry/react-router` doesn't ship a `withSentry` HOC — client-side error
// capture is wired via `<HydratedRouter onError={…}>`
// in `entry.client.tsx`, and span instrumentation runs through
// `reactRouterTracingIntegration` in `monitoring.client.tsx`.
export default function App({ loaderData }: Route.ComponentProps) {
	useRootClientEffects(loaderData);
	return <Outlet />;
}

export function ErrorBoundary(_props: Route.ErrorBoundaryProps) {
	return <GeneralErrorBoundary />;
}
